var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { Box, Chip, HelixApp, HelixPalette, HelixStyles, IExcursions, IPaperPlane, InputField, Typography } from '@ideagen/helix-react-core';
import { useTranslation } from 'react-i18next';
import { HELIX_REACT_AI_TRANSLATION_NAMESPACE } from '../../locales';
import { createChatStreamRequest } from './draggablecontainer.api';
import { useStreamingResponse } from '../../hooks/useStreamingResponse';
import { AgentConfigProvider, useAgentConfig } from '../../contexts/AgentConfigContext';
import { DraggableContainerWrapper } from './DraggableContainerComponents';
import { CitationsView } from '../CitationsView/CitationsView';
import LoadingBar from '../Animators/LoadingBar';
import MessageBox from '../MessageBox/MessageBox';
import './DraggableContainer.scss';
/**
 * Internal component that uses the agent config context
 */
const DraggableContainerInternal = ({ token, open = false, isEmbedded = false, onClose, dataId, children }) => {
    // Default constants
    const DEFAULT_MSG_ROW_COUNT = 1;
    const TEXT_AREA_INNER_PADDING_TOTAL = 20; // 12px top + 18px bottom padding
    const DEFAULT_MAX_MSG_ROW_COUNT = 5;
    // Hooks
    const { t } = useTranslation();
    const { config } = useAgentConfig();
    // Always call useAuth (React hooks rule), but use fallback when token is provided
    const authFromHook = useAuth();
    // Use fallback auth when token is provided or when auth context not available
    const auth = token ?
        { user: null, isAuthenticated: false, isLoading: false, error: null } :
        (authFromHook || { user: null, isAuthenticated: false, isLoading: false, error: null });
    const { contentBlocks, citations, newblock, replyId, threadId, errorblock, startStream, isStreaming, resetState } = useStreamingResponse();
    // Refs
    const containerBodyRef = useRef(null);
    const textareaRef = useRef(null);
    // States
    const [sessionId, setSessionId] = useState(crypto.randomUUID()); // Unique session ID for the conversation
    const [isMaximizedContainer, setIsMaximizedContainer] = useState(false);
    const [startMessageSent, setStartMessageSent] = useState(false);
    const [authToken, setAuthToken] = useState(token);
    const [authInfo, setAuthInfo] = useState({
        authToken: authToken,
        sessionId: sessionId,
    });
    const [chatStartDateTime, setChatStartDateTime] = useState(new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }));
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [messageRowCount, setMessageRowCount] = useState(DEFAULT_MSG_ROW_COUNT);
    const [isLoading, setIsLoading] = useState(false);
    const [currentStreamingId, setCurrentStreamingId] = useState(null);
    const [promptSuggestions, setPromptSuggestions] = useState(() => (config === null || config === void 0 ? void 0 : config.prompt_suggestions) || []);
    const [showCitations, setShowCitations] = useState(false);
    const [selectedMessageCitations, setSelectedMessageCitations] = useState(null);
    const [savedScrollPosition, setSavedScrollPosition] = useState(0);
    // Use Effects
    useEffect(() => {
        var _a;
        // Set token from auth if not provided and auth exists
        if (!authToken && ((_a = auth === null || auth === void 0 ? void 0 : auth.user) === null || _a === void 0 ? void 0 : _a.id_token)) {
            setAuthToken(auth.user.id_token);
        }
    }, [authToken, auth === null || auth === void 0 ? void 0 : auth.user]);
    // Update authInfo whenever authToken changes
    useEffect(() => {
        setAuthInfo({
            authToken: authToken,
            sessionId: sessionId,
        });
    }, [authToken, sessionId]);
    // Set prompt suggestions from consumer
    useEffect(() => {
        if (config === null || config === void 0 ? void 0 : config.prompt_suggestions) {
            setPromptSuggestions(config.prompt_suggestions);
        }
    }, [config === null || config === void 0 ? void 0 : config.prompt_suggestions]);
    // Scroll to bottom when new messages are added
    useEffect(() => {
        if (containerBodyRef.current && !showCitations) {
            containerBodyRef.current.scrollTop = containerBodyRef.current.scrollHeight;
        }
    }, [messages, showCitations]);
    // Restore scroll position when returning from citations view (only when not maximized)
    useEffect(() => {
        if (!showCitations && !isMaximizedContainer && savedScrollPosition > 0 && containerBodyRef.current) {
            setTimeout(() => {
                if (containerBodyRef.current) {
                    containerBodyRef.current.scrollTop = savedScrollPosition;
                }
            }, 0);
        }
    }, [showCitations, savedScrollPosition, isMaximizedContainer]);
    // Update the streaming answer message as content and citations change
    useEffect(() => {
        if (!currentStreamingId)
            return;
        setMessages((prevMessages) => {
            const idx = prevMessages.findIndex((msg) => msg.messageId === currentStreamingId);
            if (idx === -1)
                return prevMessages;
            if (errorblock) {
                const errorAnswer = {
                    messageId: currentStreamingId,
                    messageType: 'E',
                    dateTime: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
                    contentBlocks: contentBlocks,
                    rating: null,
                    userInfo: { isAgent: true, userName: 'Mazlan' },
                    question: [''],
                    isChartDataLoaded: false,
                    chartData: {},
                    citations: citations,
                };
                return [...prevMessages.slice(0, idx), errorAnswer, ...prevMessages.slice(idx + 1)];
            }
            const updatedMsg = Object.assign(Object.assign({}, prevMessages[idx]), { citations: citations.length > 0 ? [...citations] : prevMessages[idx].citations || [], isChartDataLoaded: false, chartData: {}, threadId: threadId, contentBlocks: contentBlocks });
            return [...prevMessages.slice(0, idx), updatedMsg, ...prevMessages.slice(idx + 1)];
        });
    }, [contentBlocks, citations, newblock, errorblock, currentStreamingId, threadId]);
    // Capture final citations when streaming completes
    useEffect(() => {
        if (!isStreaming && currentStreamingId && citations.length > 0) {
            setMessages((prevMessages) => prevMessages.map((msg) => (msg.messageId === currentStreamingId ? Object.assign(Object.assign({}, msg), { citations: [...citations] }) : msg)));
        }
    }, [isStreaming, currentStreamingId, citations]);
    // Textarea utility functions
    const adjustTextAreaHeight = (value) => {
        if (textareaRef.current) {
            const textarea = textareaRef.current;
            textarea.value = value;
            textarea.style.overflowY = 'hidden'; // Reset overflow to ensure accurate measurements
            const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight);
            textarea.style.height = '0'; // Temporarily reset height to get accurate scroll height
            const scrollHeight = textarea.scrollHeight;
            const contentHeight = scrollHeight - TEXT_AREA_INNER_PADDING_TOTAL;
            const linesNeeded = Math.max(DEFAULT_MSG_ROW_COUNT, Math.round(contentHeight / lineHeight));
            textarea.style.height = ''; // Reset height to let rows attribute control the height
            const rowsToShow = Math.min(linesNeeded, DEFAULT_MAX_MSG_ROW_COUNT);
            setMessageRowCount(rowsToShow);
            if (linesNeeded > 5) {
                textarea.style.overflowY = 'auto';
            }
            else {
                textarea.style.overflowY = 'hidden';
            }
        }
    };
    // Input handling functions
    const processUserInput = (inputValue) => {
        setMessageInput(inputValue);
        adjustTextAreaHeight(inputValue);
    };
    const handleInputChange = (inputValue) => {
        processUserInput(inputValue);
    };
    // Message processing functions
    const getBotResponse = useCallback((_messageId, input) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        try {
            const requestOptions = createChatStreamRequest(input, authInfo, replyId, config === null || config === void 0 ? void 0 : config.custom_settings);
            if (!(config === null || config === void 0 ? void 0 : config.agent_url)) {
                throw new Error('Agent URL must be provided in config.agent_url');
            }
            yield startStream(requestOptions, config.agent_url);
        }
        catch (error) {
            // Optionally update the answer message with error info
            setMessages((prevMessages) => {
                const idx = prevMessages.findIndex((msg) => msg.messageId === currentStreamingId);
                if (idx === -1)
                    return prevMessages;
                const updatedMsg = Object.assign(Object.assign({}, prevMessages[idx]), { messageType: 'E', contentBlocks: [{
                            id: `error-${crypto.randomUUID()}`,
                            type: 'text',
                            content: (error === null || error === void 0 ? void 0 : error.toString()) || 'Error',
                            timestamp: Date.now(),
                        }] });
                return [...prevMessages.slice(0, idx), updatedMsg, ...prevMessages.slice(idx + 1)];
            });
        }
        finally {
            setIsLoading(false);
        }
    }), [authInfo, replyId, config === null || config === void 0 ? void 0 : config.custom_settings, config === null || config === void 0 ? void 0 : config.agent_url, currentStreamingId, startStream]);
    const processMessage = useCallback((userInput) => {
        var _a, _b;
        // Check if the user input is null or empty
        if (userInput == null || userInput.trim() === '')
            return;
        const messageId = crypto.randomUUID();
        const dateTimeNow = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
        const newQuestion = {
            messageId,
            messageType: 'Q',
            dateTime: dateTimeNow,
            contentBlocks: [{
                    id: `text-${crypto.randomUUID()}`,
                    type: 'text',
                    content: userInput,
                    timestamp: Date.now(),
                }],
            rating: null,
            userInfo: { isAgent: false, userName: (_b = (_a = auth === null || auth === void 0 ? void 0 : auth.user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.name },
            question: [userInput.trim()],
            isChartDataLoaded: false,
            chartData: {},
            auth: authInfo,
        };
        const answerId = messageId + '-answer';
        const newAnswer = {
            messageId: answerId,
            messageType: 'A',
            dateTime: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
            contentBlocks: [], // Will be populated by streaming
            rating: null,
            userInfo: { isAgent: true, userName: 'Mazlan' },
            question: [userInput.trim()],
            isChartDataLoaded: false,
            chartData: {},
            citations: [],
            threadId: threadId,
            auth: authInfo,
        };
        setMessages((prev) => [...prev, newQuestion, newAnswer]);
        setCurrentStreamingId(answerId);
        setMessageInput('');
        getBotResponse(messageId, userInput);
    }, [authInfo, threadId, getBotResponse, auth === null || auth === void 0 ? void 0 : auth.user]);
    const sendMessage = (message) => {
        setPromptSuggestions([]);
        processMessage(message);
    };
    const handleSendButtonClicked = () => {
        sendMessage(messageInput);
    };
    const handleChipClicked = (inputValue) => {
        processUserInput(inputValue);
        sendMessage(inputValue);
    };
    // Message update functions
    const updateParentMessage = (updatedMessage) => {
        // Check if the updated message is valid
        if (!updatedMessage.userInfo || !updatedMessage.dateTime || !updatedMessage.question) {
            return;
        }
        // Update the message in the messages array
        setMessages((prevMessages) => prevMessages.map((message) => {
            if (message.messageId === updatedMessage.messageId && message.messageType === updatedMessage.messageType) {
                return updatedMessage;
            }
            return message;
        }));
    };
    // Container management functions
    const onResized = (isMaximized) => {
        setIsMaximizedContainer(isMaximized);
        if (textareaRef.current)
            adjustTextAreaHeight(textareaRef.current.value || '');
    };
    const handleClearChat = () => {
        setMessages([]);
        setChatStartDateTime(new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }));
        setMessageInput('');
        setSessionId(crypto.randomUUID()); // Reset session ID for a new conversation
        setShowCitations(false);
        setSelectedMessageCitations(null);
        setSavedScrollPosition(0);
        resetState(); // Reset streaming response state including replyId
        setStartMessageSent(false); // Reset start message sent flag so it can be sent again
    };
    // Citation management functions
    const handleShowCitations = (message) => {
        if (message.citations && message.citations.length > 0) {
            // If clicking on the same message that's already showing citations, close the view
            if (showCitations && (selectedMessageCitations === null || selectedMessageCitations === void 0 ? void 0 : selectedMessageCitations.messageId) === message.messageId) {
                handleBackFromCitations();
                return;
            }
            // When maximized, we don't need to save scroll position since citations go to side panel
            if (!isMaximizedContainer && containerBodyRef.current) {
                setSavedScrollPosition(containerBodyRef.current.scrollTop);
            }
            setSelectedMessageCitations({
                citations: message.citations,
                messageId: message.messageId,
                dateTime: message.dateTime || '',
            });
            setShowCitations(true);
        }
    };
    const handleBackFromCitations = () => {
        setShowCitations(false);
        setSelectedMessageCitations(null);
        // savedScrollPosition will be used by the useEffect to restore scroll position
    };
    // Handle initial message when container opens (only first time ever)
    useEffect(() => {
        if (open && (config === null || config === void 0 ? void 0 : config.start_message) && config.start_message !== '' && !startMessageSent) {
            setStartMessageSent(true);
            processMessage(config.start_message);
        }
    }, [open, config === null || config === void 0 ? void 0 : config.start_message, startMessageSent, processMessage]);
    return (_jsx(HelixApp, { children: _jsx(DraggableContainerWrapper, { open: open, onClose: onClose, onClearChat: handleClearChat, isEmbedded: isEmbedded, onResized: onResized, title: t('chatAgent.agent', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }), subTitle: t('chatAgent.poweredByAI', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }), dataId: dataId, headerStyle: { minHeight: '72px', minWidth: '400px', textAlign: 'center' }, bodyStyle: {
                height: showCitations && !isMaximizedContainer ? '865px' : '728px',
                minWidth: '436px',
                paddingTop: '1rem',
                boxSizing: 'border-box',
            }, sidePanelStyle: { width: '100%', height: '100%' }, subHeaderChildren: _jsx(LoadingBar, { dataId: `${dataId}-loading-indicator`, loading: isLoading }), containerRef: containerBodyRef, bodyChildren: _jsx(_Fragment, { children: !isMaximizedContainer && showCitations && selectedMessageCitations ? (_jsx(CitationsView, { citations: selectedMessageCitations.citations, dataId: dataId, onBack: handleBackFromCitations, authToken: authToken })) : (_jsxs(_Fragment, { children: [_jsx(MessageBox, { dataId: `${dataId}-message-initial`, messageInfo: {
                                messageId: '1',
                                messageType: 'S',
                                userInfo: { isAgent: true, userName: 'Mazlan' },
                                dateTime: chatStartDateTime,
                                contentBlocks: [{
                                        id: `welcome-${crypto.randomUUID()}`,
                                        type: 'text',
                                        content: (config === null || config === void 0 ? void 0 : config.welcome_message) || '',
                                        timestamp: Date.now(),
                                    }],
                                rating: null,
                                isChartDataLoaded: false,
                                chartData: {},
                            }, containerWidth: "100%", ActionBar: { showActionBar: false, ActionBarItems: [] } }, `${dataId}-message-initial`), (messages === null || messages === void 0 ? void 0 : messages.length) > 0 &&
                            messages.map((message, index) => (_jsx(MessageBox, { dataId: `${dataId}-message-${index}`, messageInfo: message, containerWidth: "100%", awaitingResponse: message.messageType === 'A' &&
                                    (message.messageId === currentStreamingId ||
                                        (index === messages.length - 1 && message.messageType === 'A')), ActionBar: {
                                    showActionBar: ((message.messageId !== currentStreamingId || !isStreaming) && message.messageType === 'A') || message.messageType === 'E',
                                    ActionBarItems: [
                                        ...(message.citations && message.citations.length > 0
                                            ? [
                                                _jsx(Chip, { color: "grey", dataId: `${dataId}-citations-chip-${index}`, icon: _jsx(IExcursions, {}), label: "Sources", type: "action", style: { padding: '0.25rem 0.5rem', fontSize: '12px' }, onClick: () => handleShowCitations(message), checked: showCitations && (selectedMessageCitations === null || selectedMessageCitations === void 0 ? void 0 : selectedMessageCitations.messageId) === message.messageId }, `${dataId}-citations-chip-${index}`),
                                            ]
                                            : []),
                                    ],
                                }, updateParentMessage: updateParentMessage, isLoading: isLoading && message.messageId === currentStreamingId }, `${dataId}-message-${index}`))), children] })) }), sidePanelChildren: 
            // When maximized and showing citations, display them in the side panel
            isMaximizedContainer && showCitations && selectedMessageCitations ? (_jsx(CitationsView, { citations: selectedMessageCitations.citations, dataId: dataId, onBack: handleBackFromCitations, sidePanel: true, authToken: authToken })) : null, footerStyle: { display: 'block', justifyItems: 'center', minHeight: showCitations && !isMaximizedContainer ? '0' : '137px' }, footerChildren: !isMaximizedContainer && showCitations && selectedMessageCitations ? null : (_jsxs(_Fragment, { children: [(promptSuggestions === null || promptSuggestions === void 0 ? void 0 : promptSuggestions.length) > 0 && (!(config === null || config === void 0 ? void 0 : config.start_message) || config.start_message === '') &&
                        _jsx(Box, { width: "100%", display: "flex", alignItems: "flex-start", flexDirection: "column", gap: "8px", marginBottom: "16px", maxWidth: "720px", children: promptSuggestions.map((label, index) => (_jsx(Chip, { color: "teal", dataId: `${dataId}-chip-${index}`, label: label, type: "action", style: { padding: '0.4rem 0.8rem' }, onClick: () => handleChipClicked(label) }, `${dataId}-chip-${index}`))) }), _jsx(InputField, { style: { maxWidth: '720px', width: '100%' }, ref: textareaRef, dataId: `${dataId}-user-input`, preventDefaultEnter: true, error: "", helpText: "", id: `${dataId}-user-input`, maxLength: 2000, onChange: (event) => handleInputChange(event), onEnter: () => handleSendButtonClicked(), placeholder: t('chatAgent.askQuestionPlaceHolder', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }), type: "textarea", value: messageInput, readOnly: Boolean(isLoading), inputStyle: Object.assign(Object.assign({}, HelixStyles.style_07), { scrollbarWidth: 'thin', paddingTop: '4px', paddingBottom: '4px', width: 'auto', minHeight: '10px' }), rows: messageRowCount, actionButtons: [
                            {
                                icon: _jsx(IPaperPlane, {}),
                                ariaLabel: t('commands.send', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }),
                                onClick: () => {
                                    handleSendButtonClicked();
                                },
                                disabled: !messageInput || messageInput.trim() === '',
                            },
                        ] }), _jsx(Typography, Object.assign({}, HelixStyles.style_11, { color: HelixPalette.neutral50, margin: "none", paddingTop: "8px", textAlign: "center", children: "Mazlan can make mistakes. Please check responses." }))] })) }) }));
};
/**
 * A Draggable Container to provide an intuitive and user-friendly interface for users to interact with the AI Agent
 */
export const DraggableContainer = ({ token = undefined, open = false, onClose, isEmbedded = false, dataId, config, children }) => {
    return (_jsx(AgentConfigProvider, { config: config, children: _jsx(DraggableContainerInternal, { token: token, open: open, onClose: onClose, isEmbedded: isEmbedded, dataId: dataId, children: children }) }));
};
export default DraggableContainer;
